import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';

interface AuditEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  user_id: string | null;
  user_name: string | null;
  changes: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const ACTION_STYLE: Record<string, { bg: string; color: string }> = {
  created:          { bg: '#dcfce7', color: '#16a34a' },
  updated:          { bg: '#dbeafe', color: '#2563eb' },
  deleted:          { bg: '#fee2e2', color: '#dc2626' },
  deactivated:      { bg: '#fee2e2', color: '#dc2626' },
  status_changed:   { bg: '#fef9c3', color: '#ca8a04' },
  payment_recorded: { bg: '#f0fdf4', color: '#15803d' },
  applied:          { bg: '#ede9fe', color: '#7c3aed' },
  voided:           { bg: '#fce7f3', color: '#be185d' },
  received:         { bg: '#e0f2fe', color: '#0369a1' },
  adjustment:       { bg: '#fff7ed', color: '#c2410c' },
  adjusted:         { bg: '#fff7ed', color: '#c2410c' },
  receipt:          { bg: '#dcfce7', color: '#16a34a' },
  archived:         { bg: '#f3f4f6', color: '#6b7280' },
  restored:         { bg: '#ecfdf5', color: '#059669' },
  return:           { bg: '#ede9fe', color: '#7c3aed' },
  transfer:         { bg: '#e0f2fe', color: '#0369a1' },
  supplier_return:  { bg: '#fff7ed', color: '#c2410c' },
  damage:           { bg: '#f3f4f6', color: '#374151' },
};

const ENTITY_ICONS: Record<string, string> = {
  customer: '👤',
  invoice: '🧾',
  order: '🛒',
  product: '📦',
  supplier: '🏭',
  booking: '📅',
  credit_note: '🎫',
  purchase_order: '📋',
  stock_movement: '🔄',
  category: '🗂️',
  staff: '🧑‍💼',
};

const ENTITY_PATHS: Record<string, string> = {
  customer: '/customers',
  invoice: '/invoices',
  order: '/orders',
  product: '/products',
  supplier: '/suppliers',
  booking: '/booking',
  credit_note: '/credit-notes',
  purchase_order: '/purchase-orders',
  stock_movement: '/stock-movements',
  category: '/categories',
  staff: '/staff',
};

const ALL_ENTITY_TYPES = Object.keys(ENTITY_ICONS);
const ALL_ACTIONS = ['created','updated','deleted','deactivated','status_changed','payment_recorded',
  'applied','voided','received','adjusted','receipt','archived','restored','return',
  'transfer','supplier_return','damage'];

function formatChanges(changes: Record<string, unknown> | null): React.ReactNode {
  if (!changes) return <span style={{ color: '#9ca3af' }}>—</span>;
  const entries = Object.entries(changes);
  if (entries.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ fontSize: 12 }}>
      {entries.slice(0, 4).map(([k, v]) => (
        <div key={k} style={{ marginBottom: 2 }}>
          <span style={{ color: '#6b7280', fontWeight: 500 }}>{k}: </span>
          <span style={{ color: '#111' }}>
            {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
          </span>
        </div>
      ))}
      {entries.length > 4 && <div style={{ color: '#9ca3af' }}>+{entries.length - 4} more...</div>}
    </div>
  );
}

function exportCSV(entries: AuditEntry[]) {
  const header = ['Date', 'User', 'Action', 'Module', 'Entity ID', 'Changes'];
  const rows = entries.map(e => [
    new Date(e.created_at).toLocaleString('en-GB'),
    e.user_name || 'System',
    e.action,
    e.entity_type,
    e.entity_id || '',
    e.changes ? JSON.stringify(e.changes) : '',
  ]);
  const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const AuditLog: React.FC = () => {
  const { token, role } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(100);
  const [listPage, setListPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState('');

  const fetchAuditLog = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setFetchError('');
    try {
      const params = new URLSearchParams();
      if (entityType) params.set('entity_type', entityType);
      if (action) params.set('action', action);
      if (userSearch.trim()) params.set('user_name', userSearch.trim());
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      params.set('limit', String(limit));

      const res = await apiFetch(`${API_URL}/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setEntries(data.entries ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error(err);
      setFetchError('Failed to load audit log. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [token, entityType, action, userSearch, fromDate, toDate, limit]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setLimit(prev => prev + 100);
  }, []);

  useEffect(() => {
    if (role === 'Admin') fetchAuditLog();
  }, [role, fetchAuditLog]);

  useEffect(() => {
    setListPage(1);
  }, [entityType, action, userSearch, fromDate, toDate, limit]);

  if (role !== 'Admin') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#374151' }}>Admin Access Only</div>
        <div style={{ color: '#6b7280', marginTop: 8 }}>You do not have permission to view the audit log.</div>
      </div>
    );
  }

  const filtered = entries;
  const { pages: auditPages, safePage: safeListPage, slice: paginatedEntries } =
    paginateList(filtered, listPage);

  const s: Record<string, React.CSSProperties> = {
    page: { padding: '24px', maxWidth: 1400, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
    filterCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', flexWrap: 'wrap' as const, gap: 12, alignItems: 'center' },
    select: { padding: '7px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, background: '#fff', outline: 'none', cursor: 'pointer' },
    input: { padding: '7px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none' },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: { padding: '10px 14px', fontWeight: 600, fontSize: 12, color: '#6b7280', textAlign: 'left' as const, borderBottom: '2px solid #e5e7eb', background: '#f9fafb', textTransform: 'uppercase' as const, letterSpacing: '0.05em' },
    td: { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' as const },
    btn: { padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>📋 Audit Log</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
            Complete history of every action taken in your account
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => exportCSV(filtered)} style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
            ⬇ Export CSV
          </button>
          <button onClick={fetchAuditLog} style={{ ...s.btn, background: '#3b82f6', color: '#fff' }}>
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {fetchError && (
        <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          {fetchError}
        </div>
      )}

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Records', value: total, color: '#3b82f6' },
          { label: 'Showing', value: filtered.length, color: '#10b981' },
          { label: 'Users Active', value: new Set(entries.map(e => e.user_name).filter(Boolean)).size, color: '#8b5cf6' },
          { label: 'Modules Covered', value: new Set(entries.map(e => e.entity_type)).size, color: '#f59e0b' },
        ].map(stat => (
          <div key={stat.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 18px', textAlign: 'center', flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={s.filterCard}>
        <select value={entityType} onChange={e => setEntityType(e.target.value)} style={s.select}>
          <option value="">All Modules</option>
          {ALL_ENTITY_TYPES.map(t => (
            <option key={t} value={t}>{ENTITY_ICONS[t]} {t.replace('_', ' ')}</option>
          ))}
        </select>

        <select value={action} onChange={e => setAction(e.target.value)} style={s.select}>
          <option value="">All Actions</option>
          {ALL_ACTIONS.map(a => <option key={a} value={a}>{a.replace('_', ' ')}</option>)}
        </select>

        <input
          value={userSearch}
          onChange={e => setUserSearch(e.target.value)}
          placeholder="Search by user name..."
          style={{ ...s.input, minWidth: 180 }}
        />

        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={s.input} />
        <span style={{ color: '#9ca3af', fontSize: 13 }}>to</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={s.input} />

        <select value={limit} onChange={e => setLimit(Number(e.target.value))} style={s.select}>
          {[50, 100, 250, 500].map(n => <option key={n} value={n}>{n} rows</option>)}
        </select>

        {(entityType || action || userSearch || fromDate || toDate) && (
          <button onClick={() => { setEntityType(''); setAction(''); setUserSearch(''); setFromDate(''); setToDate(''); }}
            style={{ ...s.btn, background: 'none', color: '#6b7280', border: '1px solid #d1d5db', fontWeight: 400 }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* Table */}
      <ListPagination
        total={total}
        filtered={filtered.length}
        page={safeListPage}
        pages={auditPages}
        onPageChange={setListPage}
        noun="entries"
      />
      <div className="list-table-panel">
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading audit log...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            <div style={{ color: '#6b7280' }}>No audit entries found for these filters.</div>
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {['Date & Time', 'User', 'Action', 'Module', 'Entity', 'Details'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedEntries.map((entry, idx) => {
                const actionStyle = ACTION_STYLE[entry.action] || { bg: '#f3f4f6', color: '#6b7280' };
                const icon = ENTITY_ICONS[entry.entity_type] || '📄';
                const isExpanded = expandedId === entry.id;
                return (
                  <React.Fragment key={entry.id}>
                    <tr style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                          {new Date(entry.created_at).toLocaleDateString('en-GB')}
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: 11 }}>
                          {new Date(entry.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                      </td>
                      <td style={s.td}>
                        <div style={{ fontWeight: 500 }}>{entry.user_name || <span style={{ color: '#9ca3af' }}>System</span>}</div>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...actionStyle, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block' }}>
                          {entry.action.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td style={s.td}>
                        <span style={{ fontWeight: 500 }}>{icon} {entry.entity_type.replace(/_/g, ' ')}</span>
                      </td>
                      <td style={s.td}>
                        {entry.entity_id ? (
                          <a
                            href={`${ENTITY_PATHS[entry.entity_type] || ''}/${entry.entity_id}`}
                            onClick={e => e.stopPropagation()}
                            style={{ color: '#3b82f6', fontFamily: 'monospace', fontSize: 12, textDecoration: 'none' }}
                          >
                            #{entry.entity_id}
                          </a>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {formatChanges(entry.changes)}
                          {entry.changes && <span style={{ color: '#d1d5db', fontSize: 14 }}>{isExpanded ? '▲' : '▼'}</span>}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && entry.changes && (
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan={6} style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb' }}>
                          <pre style={{ fontSize: 12, margin: 0, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(entry.changes, null, 2)}
                          </pre>
                          {(entry.ip_address || entry.user_agent) && (
                            <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
                              {entry.ip_address && <div>IP: {entry.ip_address}</div>}
                              {entry.user_agent && <div style={{ marginTop: 2, wordBreak: 'break-all' }}>UA: {entry.user_agent}</div>}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', color: '#9ca3af', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Showing {filtered.length} of {total} total entries</span>
          {filtered.length < total && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              style={{ padding: '6px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: loadingMore ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {loadingMore ? 'Loading...' : `Load more (${total - filtered.length} remaining)`}
            </button>
          )}
        </div>
      </div>
      </div>

      <div className="list-mobile-cards">
        {paginatedEntries.map(entry => {
          const actionStyle = ACTION_STYLE[entry.action] || { bg: '#f3f4f6', color: '#6b7280' };
          const icon = ENTITY_ICONS[entry.entity_type] || '📄';
          return (
            <div key={entry.id} className="list-mobile-card" onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)} style={{ cursor: 'pointer' }}>
              <div className="list-mobile-card-header">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="list-mobile-card-title">{icon} {entry.entity_type.replace(/_/g, ' ')}</div>
                  <div className="list-mobile-card-meta">{entry.user_name || 'System'} · {new Date(entry.created_at).toLocaleString('en-GB')}</div>
                </div>
                <span style={{ ...actionStyle, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                  {entry.action.replace(/_/g, ' ').toUpperCase()}
                </span>
              </div>
              <div className="list-mobile-card-info">
                <div><div className="list-mobile-card-label">Entity</div>#{entry.entity_id || '—'}</div>
              </div>
              {entry.changes && (
                <div style={{ fontSize: 12, color: '#374151', marginTop: 8 }}>{formatChanges(entry.changes)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuditLog;
